import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo
} from "react";
import { ReservedUserIds, UserData } from "@/entities/users/types";
import { useUsers } from "@/entities/users/hooks";
import useBoundStore from "@/services/store/BoundStore";
import useAPI from "@/services/api/hook";
import {
  ReportType,
  ScoringForDirectReportsResponse,
  SettingsPeriod
} from "@/entities/reporting/types";
import { ScoringTableDataRow } from "@/components/controls/DataTable/ScoringTable";
import { isEmpty } from "lodash";

export interface BaseScoringTableContextValue {
  selectedUserId: string;
  setSelectedUserId: (id: string) => void;
  dynamicTableData: ScoringTableDataRow[] | undefined;
  breadcrumbIds: string[];
  handleRowClick: (rowData: ScoringTableDataRow) => void;
  directReportsData?: ScoringForDirectReportsResponse;
  isLoading: boolean;
  error: unknown;
  refetch: () => void;
}

export const ScoringTableContext = createContext<
  BaseScoringTableContextValue | undefined
>(undefined);

export function createScoringTableContext() {
  return {
    ScoringTableProvider({
      children,
      leadUser,
      period,
      reportType,
      defaultPeriod,
      transformResponse
    }: {
      children: React.ReactNode;
      leadUser?: UserData;
      period?: SettingsPeriod;
      reportType: ReportType;
      defaultPeriod?: SettingsPeriod;
      transformResponse: (
        data: ScoringForDirectReportsResponse
      ) => ScoringTableDataRow[];
    }) {
      const getUser = useBoundStore((state) => state.users.getEntity);
      const [selectedUserId, setSelectedUserId] = useState<string>(
        ReservedUserIds.UNKNOWN_USER_ID
      );

      useEffect(() => {
        setSelectedUserId(leadUser?.id ?? ReservedUserIds.UNKNOWN_USER_ID);
      }, [leadUser]);

      const selectedUser = useUsers([selectedUserId]).users[0];
      const directReports = useMemo(
        () => selectedUser?.directReports ?? [],
        [selectedUser]
      );
      const directReportsData = useUsers(directReports).users;

      const resolvedPeriod = period ?? defaultPeriod;

      const { data, isLoading, error, refetch } = useAPI(
        "reports/type/directReports",
        {
          pathParams: { type: reportType.toString() },
          queryParams: {
            user: selectedUserId,
            ...(resolvedPeriod !== undefined && {
              period: resolvedPeriod.toString()
            })
          }
        },
        {
          retry: false,
          staleTime: 0,
          enabled:
            selectedUserId !== ReservedUserIds.UNKNOWN_USER_ID &&
            !isEmpty(directReports)
        }
      );

      const handleRowClick = (rowData: ScoringTableDataRow) => {
        const directReportsLength = rowData.user?.directReports?.length ?? 0;
        if (directReportsLength > 0 && rowData.user?.id) {
          setSelectedUserId(rowData.user.id);
        }
      };

      const dynamicTableData = useMemo(() => {
        if (!leadUser?.directReports?.length) return undefined;

        if (!directReportsData || !data) return [];

        const transformedData = transformResponse(data);
        return transformedData.map((row) => ({
          ...row,
          user:
            directReportsData.find((user) => user.id === row.user?.id) ??
            row.user
        }));
      }, [directReportsData, data, transformResponse, leadUser]);

      const breadcrumbIds = useMemo(() => {
        const buildPath = (
          user: UserData | undefined,
          lead: UserData | undefined,
          path: string[] = []
        ): string[] => {
          if (!user || !lead) return path;
          path.unshift(user.id);
          if (user.id === lead.id || !user.managerId) return path;
          return buildPath(getUser(user.managerId), lead, path);
        };

        return buildPath(selectedUser, leadUser);
      }, [selectedUser, leadUser, getUser]);

      const value = {
        selectedUserId,
        setSelectedUserId,
        dynamicTableData,
        breadcrumbIds,
        handleRowClick,
        directReportsData: data,
        isLoading,
        error,
        refetch
      };

      return (
        <ScoringTableContext.Provider value={value}>
          {children}
        </ScoringTableContext.Provider>
      );
    }
  };
}

export function useScoringTable() {
  const context = useContext(ScoringTableContext);
  if (!context) {
    throw new Error(
      "useScoringTable must be used within a ScoringTableProvider"
    );
  }
  return context;
}
